import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { GetMyPassengerResponse } from '../../types/auth.js'
import type { CreatePassengerRequest } from '../../types/client.js'

import { apiClient } from '../../lib/client.js'
import { meKeys } from './keys.js'

export const useCreateMyPassengerMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreatePassengerRequest) => {
      const response = await apiClient.client.post<GetMyPassengerResponse>('/me/passengers', data)
      return response.data
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: meKeys.passengers })
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: meKeys.passengers })

      const previousPassengers = queryClient.getQueryData<GetMyPassengerResponse[]>(
        meKeys.passengers
      )

      if (previousPassengers) {
        const optimisticPassenger: GetMyPassengerResponse = {
          clientId: '',
          firstName: data.firstName,
          id: `temp-${Date.now()}`,
          lastName: data.lastName,
          birthDate: data.birthDate,
          fidelityCards: [],
          gender: data.gender,
          identityDocuments: [],
          nationalityCode: data.nationalityCode,
          nationalityRef: null,
          updatedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
        }

        queryClient.setQueryData<GetMyPassengerResponse[]>(meKeys.passengers, [
          ...previousPassengers,
          optimisticPassenger,
        ])
      }

      return { previousPassengers }
    },
    onSuccess: (newPassenger) => {
      queryClient.setQueryData<GetMyPassengerResponse[]>(
        meKeys.passengers,
        (old) => old?.map((p) => (p.id.startsWith('temp-') ? newPassenger : p)) ?? [newPassenger]
      )
    },
  })
}
